import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { CheckCircle, Circle, X, Clock, CheckSquare } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function StudentProgressDrilldown({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({}); // keyed by `${week}-${idx}` for per-task spinners

  const load = (id) => {
    setData(null);
    setError("");
    api
      .get(`/admin/users/${id}/progress`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load progress"));
  };

  useEffect(() => {
    if (!userId) return;
    load(userId);
  }, [userId]);

  const pct = useMemo(() => {
    if (!data || !data.total_tasks) return 0;
    return Math.round((data.verified_tasks / data.total_tasks) * 100);
  }, [data]);

  const setTaskBusy = (key, val) =>
    setBusy((prev) => ({ ...prev, [key]: val }));

  const toggleVerify = async (week_num, task_index, nextVerified) => {
    const key = `${week_num}-${task_index}`;
    setTaskBusy(key, true);
    try {
      await api.post("/admin/progress/verify", {
        user_id: userId,
        week_num,
        task_index,
        verified: nextVerified,
      });
      load(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update verification");
    } finally {
      setTaskBusy(key, false);
    }
  };

  const verifyAllPending = async () => {
    if (!data?.pending_tasks) return;
    setBusy({ _bulk: true });
    try {
      const { data: res } = await api.post("/admin/progress/bulk-verify", { user_id: userId });
      toast.success(
        res.verified_count === 1
          ? "1 task verified"
          : `${res.verified_count} tasks verified`,
      );
      load(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk verify failed");
    } finally {
      setBusy({});
    }
  };

  if (!userId) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose}
        data-testid="drilldown-overlay"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold tracking-wider uppercase text-slate-500">Progress drilldown</div>
              <h2 className="font-outfit text-2xl font-bold truncate">
                {data ? data.user.name : "Loading..."}
              </h2>
              {data && (
                <div className="flex flex-wrap gap-2 mt-2 text-xs font-medium">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-800" data-testid="drilldown-verified-pill">
                    <CheckCircle size={12} weight="fill" /> {data.verified_tasks} verified
                  </span>
                  {data.pending_tasks > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-100 text-amber-900" data-testid="drilldown-pending-pill">
                      <Clock size={12} weight="fill" /> {data.pending_tasks} pending review
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-slate-100 text-slate-700">
                    {data.total_tasks} total · {pct}%
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {data && data.pending_tasks > 0 && (
                <button
                  onClick={verifyAllPending}
                  disabled={!!busy._bulk}
                  data-testid="drilldown-bulk-verify-btn"
                  className="btn-tactile-yellow inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-xs hover:bg-yellow-500 disabled:opacity-60"
                >
                  <CheckSquare size={14} weight="bold" />
                  {busy._bulk ? "Verifying..." : `Verify all ${data.pending_tasks}`}
                </button>
              )}
              <button
                onClick={onClose}
                data-testid="drilldown-close"
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
          </div>

          {error && (
            <div className="p-6 text-red-600 text-sm" data-testid="drilldown-error">
              {error}
            </div>
          )}

          {data && (
            <div className="overflow-y-auto p-6 space-y-4">
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              {data.weeks.map((w) => {
                const verifiedInWeek = w.tasks.filter((t) => t.verified).length;
                const pendingInWeek = w.tasks.filter((t) => t.submitted && !t.verified).length;
                return (
                  <div
                    key={w.week_num}
                    className="bg-white rounded-2xl border border-slate-200 p-4"
                    data-testid={`drilldown-week-${w.week_num}`}
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="min-w-0">
                        <div className="text-xs font-bold tracking-wider uppercase text-slate-500">
                          Week {w.week_num}
                        </div>
                        <div className="font-outfit font-bold truncate">{w.title}</div>
                      </div>
                      <div className="text-xs font-semibold flex items-center gap-2 shrink-0">
                        <span className="text-emerald-700">{verifiedInWeek}/{w.tasks.length}</span>
                        {pendingInWeek > 0 && (
                          <span className="text-amber-700">+{pendingInWeek} pending</span>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-1.5 mt-2">
                      {w.tasks.map((t, idx) => {
                        const key = `${w.week_num}-${idx}`;
                        const isPending = t.submitted && !t.verified;
                        const isBusy = !!busy[key];
                        return (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm py-1"
                            data-testid={`drilldown-task-${w.week_num}-${idx}`}
                          >
                            {t.verified ? (
                              <CheckCircle weight="fill" size={18} className="text-emerald-500 mt-0.5 shrink-0" data-testid={`drilldown-icon-verified-${w.week_num}-${idx}`} />
                            ) : isPending ? (
                              <Clock weight="fill" size={18} className="text-amber-500 mt-0.5 shrink-0" data-testid={`drilldown-icon-pending-${w.week_num}-${idx}`} />
                            ) : (
                              <Circle size={18} className="text-slate-300 mt-0.5 shrink-0" />
                            )}
                            <span
                              className={
                                t.verified
                                  ? "line-through text-slate-400 flex-1"
                                  : isPending
                                    ? "text-slate-800 flex-1"
                                    : "text-slate-800 flex-1"
                              }
                            >
                              {t.text}
                            </span>
                            {isPending && (
                              <button
                                onClick={() => toggleVerify(w.week_num, idx, true)}
                                disabled={isBusy}
                                data-testid={`drilldown-verify-btn-${w.week_num}-${idx}`}
                                className="text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-60 shrink-0"
                              >
                                {isBusy ? "..." : "Verify"}
                              </button>
                            )}
                            {t.verified && (
                              <button
                                onClick={() => toggleVerify(w.week_num, idx, false)}
                                disabled={isBusy}
                                data-testid={`drilldown-unverify-btn-${w.week_num}-${idx}`}
                                className="text-[11px] font-semibold px-2 py-0.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-60 shrink-0"
                                title="Undo verification"
                              >
                                Undo
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
